import {
  CalendarCheckIcon,
  GiftIcon,
  HouseSimpleIcon,
  InfoIcon,
  KeyIcon,
  ListChecksIcon,
  PackageIcon,
  UserListIcon,
  UsersIcon,
  WrenchIcon,
} from '@phosphor-icons/react'
import { TFunction } from 'i18next'

import { SideNavGroup, SideNavItem } from '@/components/layout/side-nav'

import { ClientProfileTab, ClientProfileTabEnum } from './client-profile.types'

export const CLIENT_PROFILE_TAB = {
  Activity: ClientProfileTabEnum.enum.activity,
  Amenities: ClientProfileTabEnum.enum.amenities,
  GeneralInformation: ClientProfileTabEnum.enum['general-information'],
  GiftCards: ClientProfileTabEnum.enum['gift-cards'],
  Inspections: ClientProfileTabEnum.enum.inspections,
  KeylessAccess: ClientProfileTabEnum.enum['keyless-access'],
  Maintenance: ClientProfileTabEnum.enum.maintenance,
  Packages: ClientProfileTabEnum.enum.packages,
  Reservations: ClientProfileTabEnum.enum.reservations,
  Visitors: ClientProfileTabEnum.enum.visitors,
} as const satisfies Record<string, ClientProfileTab>

export const VALID_TABS = ClientProfileTabEnum.options

type NavContext = {
  amenityPassesAvailable: boolean
  isTrainer: boolean
  moduleAvailability: Partial<Record<ClientProfileTab, boolean>>
}

export const getNavGroups = (t: TFunction<['clients']>): SideNavGroup[] => [
  { key: 'Operations', label: t('clients:profile.nav.groups.operations') },
  { key: 'Engagement', label: t('clients:profile.nav.groups.engagement') },
]

const isTabHidden = (
  slug: ClientProfileTab,
  { amenityPassesAvailable, isTrainer, moduleAvailability }: NavContext,
) => {
  if (slug === CLIENT_PROFILE_TAB.Activity && isTrainer) return true
  if (slug === CLIENT_PROFILE_TAB.KeylessAccess && isTrainer) return true
  if (slug === CLIENT_PROFILE_TAB.Amenities && !amenityPassesAvailable) return true
  return moduleAvailability[slug] === false
}

export const getNavItems = (
  t: TFunction<['clients']>,
  context: NavContext,
): SideNavItem<ClientProfileTab>[] =>
  [
    {
      icon: InfoIcon,
      label: t('clients:profile.nav.generalInformation'),
      slug: CLIENT_PROFILE_TAB.GeneralInformation,
    },
    {
      icon: UserListIcon,
      label: t('clients:profile.nav.activity'),
      slug: CLIENT_PROFILE_TAB.Activity,
    },
    {
      group: 'Operations',
      icon: KeyIcon,
      label: t('clients:profile.nav.keylessAccess'),
      slug: CLIENT_PROFILE_TAB.KeylessAccess,
    },
    // Note: idea EL-14178 orders Operations as Maintenance, Tasks, Packages, Visitors.
    {
      group: 'Operations',
      icon: WrenchIcon,
      label: t('clients:profile.nav.maintenance'),
      slug: CLIENT_PROFILE_TAB.Maintenance,
    },
    {
      group: 'Operations',
      icon: ListChecksIcon,
      label: t('clients:profile.nav.tasks'),
      slug: CLIENT_PROFILE_TAB.Inspections,
    },
    {
      group: 'Operations',
      icon: PackageIcon,
      label: t('clients:profile.nav.packages'),
      slug: CLIENT_PROFILE_TAB.Packages,
    },
    {
      group: 'Operations',
      icon: UsersIcon,
      label: t('clients:profile.nav.visitors'),
      slug: CLIENT_PROFILE_TAB.Visitors,
    },
    {
      group: 'Operations',
      icon: CalendarCheckIcon,
      label: t('clients:profile.nav.reservations'),
      slug: CLIENT_PROFILE_TAB.Reservations,
    },
    // Note: idea EL-14178 orders Engagement as Amenities, ..., Gift Cards - the "..." modules
    // (Events, Services, Loyalty, Community groups, Surveys, Communication) aren't in scope.
    {
      group: 'Engagement',
      icon: HouseSimpleIcon,
      label: t('clients:profile.nav.amenities'),
      slug: CLIENT_PROFILE_TAB.Amenities,
    },
    {
      group: 'Engagement',
      icon: GiftIcon,
      label: t('clients:profile.nav.giftCards'),
      slug: CLIENT_PROFILE_TAB.GiftCards,
    },
  ].map((item) => ({ ...item, hidden: isTabHidden(item.slug, context) }))
